import { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import { RootState } from "../store";

// Magic components
import MagicTableHeader from "../components/common/MagicTableHeader";
import MagicDrawer from "../components/common/MagicDrawer_new";

// @mui
import Button from "../components/mui/Button";
import Stack from "../components/mui/Stack";
import Box from "../components/mui/Box";

// Components:
import PerformanceIndicatorsGraph from "../components/dashboard/PerformanceIndicatorsGraph";
import IndividualIndicatorsGraph from "../components/dashboard/IndividualIndicatorsGraph";
import CentresPerformanceTable from "../components/dashboard/CentresPerformanceTable";
import QuarterWiseMedalsGraph from "../components/dashboard/QuarterWiseMedalsGraph";
import RegionWiseMedalsGraph from "../components/dashboard/RegionWiseMedalsGraph";
import HeatMapTable from "../components/dashboard/HeatMapTable";
import FilterForm from "../components/dashboard/FilterForm";
import KPICards from "../components/dashboard/KPICards";


// Common components:
import MagicPageHeader from "../components/common/MagicPageHeader";

// APIs
import {
  useGetScoreCardsQuery,
  useGetRegionWiseModalsGraphQuery,
  useGetQuarterWiseMedalsGraphQuery,
  useGetIndividualIndicatorsGraphQuery,
  useGetPerformanceIndicatorsGraphQuery,
  useLazyGetPerformanceTableQuery,
  useGetHitMapTableQuery,

} from "../store/apis/dashboardApis";

// Filter APIs
import { useCenterListQuery,  useFetchedFunderQuery, useUserListQuery,  } from "../store/apis/centerApis";
import usePermission from "../helper/usePermission";
import { acl } from "../helper/userAccessConstant";
import { City, State } from "country-state-city";
import helperMethods from "../helper/helperMethods";
import { useLocation } from "react-router-dom";


export type KPICard = {
  awardId: number;
  awardName: string;
  count: number;
  percentage: number;
};

interface IndianCity {
  label: string;
  id: string;
}

type Cities = IndianCity[];


const performanceGraphType = {
  performanceParameter: "Programme performance",
  qualityParameter: "Quality and efficacy",
};


const DashboardContainer = () => {

  const isUserManagemnt = helperMethods.isUserManagemnt();

  const location = useLocation()


  const selectedFilters = useSelector((state: RootState) => state.user.filter);       // Fetching master filter from Redux state:

  const [selectedKPICard, setSelectedKPICard] = useState<number>();                   // Store active KPI card id:
  const [activeKPICardName, setActiveKPICardName] = useState<string>("all centres");  // Store active KPI card name:
  const [activeKPICardCount, setactiveKPICardCount] = useState<number>(0);
  const [isOpenFilterForm, setIsOpenFilterForm] = useState<boolean>(false);           // Store state of filter on open and closed
  const [activeCentresPerformanceTable, setActiveCentresPerformanceTable] = useState(performanceGraphType.performanceParameter);
  const [centresPerformanceTableData, setCentresPerformanceTableData] = useState([]); // store table data based on performance to quality
  const [activeCentresPerformanceHeaderBtnId, setActiveCentresPerformanceHeaderBtnId] = useState<number>(0);
  const [performIndicatorsGraphCategories, setPerformIndicatorsGraphCategories] = useState([]);
  const [performIndicatorsGraphSeriesData, setPerformIndicatorsGraphSeriesData] = useState([]);
  const [centresPerformanceTablePagination, setCentresPerformanceTablePagination] = useState(
    {pageNo: 1, pageSize: 10,}
  );
  const [centresPerformanceTableSearch, setCentresPerformanceTableSearch] = useState<string>("");
  const [isInvalidCentresPerformanceTabSearch, setIsInvalidCentresPerformanceTabSearch] = useState<boolean>(false);
  const [hitMapTablePagination, setHitMapTablePagination] = useState<any>({pageNo: "1",pageSize: "10",});
  const [cities, setCities] = useState<Cities>([]);

  const indianStates: { label: string; id: string }[] =
    State.getStatesOfCountry("IN")?.map((state:any) => ({
      label: state.name,
      id: state?.isoCode
    })) || [];
  

  // access for dashboard table
  const { permissions = {}, isAdmin } = usePermission();
  
  const dashboardKPIAccess = permissions[acl.dashboard.module]?.dashboard_kpi;
  const regionWiseGraphAccess = permissions[acl.dashboard.module]?.region_wise_medals_graph;
  const QuarterWiseMedalsGraphAccess = permissions[acl.dashboard.module]?.quarter_wise_medals_graph;
  const IndividualIndicatorsGraphAccess = permissions[acl.dashboard.module]?.individual_indicators_graph;
  const PerformanceIndicatorsGraphAccess = permissions[acl.dashboard.module]?.performance_indicators_graph;
  const HeatMapTableAccess = permissions[acl.dashboard.module]?.heat_map_table;
  const performanceOfAllCentresMedalsAccess = permissions[acl.dashboard.module]?.performance_of_all_centres_medals;

  // const [commonApiPayload, setCommonApiPayload] = useState<{[key: string]: string | number | null}>({
  //   region: "",
  //   medals: "",
  //   state: "",
  //   city: "",
  //   // reginalDataManager: "",
  //   // cityManager: "",
  //   // clusterManager: "",
  //   funder: "",
  //   centerId: "",
  //   mcpCenter: "",
  // });
  const [commonApiPayload, setCommonApiPayload] = useState<Record<string, string | number | null>>({});


  const [filter, setFilter] = useState<any>({
    region: "",
    medals: "",
    state: "",
    city: "",
    funder: "",
    centres:"",
    mcpCenter: "",
  });


  // Selected master filters:
  const programMasterId = selectedFilters?.selectedProgram?.value||null;
  const quarterMasterId  = selectedFilters?.selectedQuarter?.quarterMasterId||null;
  const ProgramYearId  = selectedFilters?.selectedYear?.value||null;

  // Determine whether to skip API calls
  const shouldFetch = programMasterId && ProgramYearId && quarterMasterId;
  

  // APIs calling
  const { data: kpiCardData = {}, isLoading: isKPICardLoading, refetch: refetchKpiCard } = useGetScoreCardsQuery(
    { programMasterId, quarterMasterId, ProgramYearId },
    { skip: !shouldFetch }
  );
  
  const { data: regionWiseModalsGraphRes = {}, isLoading: regionWiseModalsLoading, refetch: refetchRegionWiseModalsGraph } = useGetRegionWiseModalsGraphQuery(
    { programMasterId, quarterMasterId, ProgramYearId, ...commonApiPayload },
    { skip: !shouldFetch }
  );
  
  const { data: quarterGraphData = [], isLoading: isQuarterGraphLoading, refetch: refetchQuarterGraph } = useGetQuarterWiseMedalsGraphQuery(
    { programMasterId, quarterMasterId, ProgramYearId, ...commonApiPayload },
    { skip: !shouldFetch }
  );
  
  const { data: individualIndicatorsGraphRes = {}, isLoading: isIndividualIndicatorGraphLoading, refetch: refetchIndividualIndicatorsGraph } = useGetIndividualIndicatorsGraphQuery(
    { programMasterId, quarterMasterId, ProgramYearId, ...commonApiPayload },
    { skip: !shouldFetch }
  );
  
  const { data: performanceIndicatorsGraphRes = {}, isLoading: isPerformanceIndicatorsGraphLoading, refetch: refetchPerformanceIndicatorsGraph } = useGetPerformanceIndicatorsGraphQuery(
    { programMasterId, quarterMasterId, ProgramYearId, ...commonApiPayload },
    { skip: !shouldFetch }
  );
  
  const { data: hitMapData = {}, isLoading: isHitMapTableLoading, refetch: refetchHitMapTable } = useGetHitMapTableQuery(
    { programMasterId, quarterMasterId, ProgramYearId, ...hitMapTablePagination, ...commonApiPayload },
    { skip: !shouldFetch }
  );
  
  const [
    fetchPerformanceTable,
    { data: performanceTableRes = {}, isLoading: isPerformanceTableLoading },
  ] = useLazyGetPerformanceTableQuery();

  // Dashboard Filter APIs
  const {data: centresList = [] } = useCenterListQuery({
    state: filter.state ? filter?.state?.label : undefined,
    district: filter.city ? filter?.city?.label : undefined,
    region: filter.region ? filter?.region : undefined,
    pageNo: "1",
    pageSize: "10000",
  },
  { refetchOnMountOrArgChange: true });
  const {data: fundersList = [] } = useFetchedFunderQuery();

  useEffect(() => {  
    // Call the API and wait for the response
    if(shouldFetch){
      fetchPerformanceTable({ 
        programMasterId, 
        quarterMasterId, 
        ProgramYearId, 
        manageAwardId : selectedKPICard,
        pageNo: centresPerformanceTablePagination.pageNo,
        pageSize: centresPerformanceTablePagination.pageSize,
        search: centresPerformanceTableSearch,
        ...commonApiPayload,
      });
      refetchKpiCard()
      refetchRegionWiseModalsGraph()
      refetchQuarterGraph()
      refetchIndividualIndicatorsGraph()
      refetchPerformanceIndicatorsGraph()
      refetchHitMapTable()
    } 

  }, [
    programMasterId, 
    quarterMasterId, 
    ProgramYearId, 
    selectedKPICard,
    centresPerformanceTablePagination,
    commonApiPayload,
    centresPerformanceTableSearch,
    selectedFilters,
  ]);


  useEffect(() => {  
    // @ts-ignore
    setPerformIndicatorsGraphCategories(performanceIndicatorsGraphRes.performanceIndicatorsCategories);
    // @ts-ignore
    setPerformIndicatorsGraphSeriesData(performanceIndicatorsGraphRes.performanceIndicatorsSeriesData);
    
  }, [performanceIndicatorsGraphRes]); 


  
  // consents:

  // @ts-ignore
  const totalCentersCount: number = kpiCardData?.totalCenters ?? 0;

  // @ts-ignore
  const kpiCards: KPICard[] = kpiCardData?.cardDetails || []; 
  


  
  const activeCardStatusId = {
    all_centres: "all centres",
    platinum: 2,
    gold: 3,
    silver: 4,
    bronze: 5,
  };

  const individualIndicatorsGraphQuarters = [
    { quarter: "Current quarter", color: "#165DFF" },
    { quarter: "Previous quarter", color: "#F7BA1E" },
    { quarter: "Prior quarter", color: "#14C9C9" },
  ];

  const performanceWiseStatusInfo = [
    { text: "Need attention", color: "#E75A47" },
    { text: "Doing well", color: "#54C95B" },
  ];

  const performanceWiseParameterBtnInfo = [
    { id: 0, name: "Performance parameter" },
    { id: 1, name: "Quality parameter" },
  ];

  const ratioBtn = [
    { label: "All", value: "" },
    { label: "East", value: "East" },
    { label: "West", value: "West" },
    { label: "North", value: "North" },
    { label: "South", value: "South" },
  ];
  
  // @ts-ignore
  const medals = kpiCards.map(c => ({label: c.awardName, id: c.awardId})) || []

  const filterFormValueAndLabel = [
    { 
      name: "medals", label: "Medals", options: medals},
    { name: "state", label: "State", options: indianStates },
    { name: "city", label: "City", options: cities },
    { name: "centres", label: "Centre", options: centresList?.centerSelect },
    { name: "funder", label: "Funder", options: fundersList },
    { 
      name: "mcpCenter", 
      label: "Mcp Centre", 
      options: [{label:"Yes", id: true}, {label:"No", id: false}]
    },
  ]

  const heatMapTableStatusBtn = [
    { name: "Need Attention", color: "#E75A47" },
    { name: "Partial Achieved", color: "#FFC85D" },
    { name: "Achieved Target", color: "#54C95B" },
  ]
  

  /**
   * @Use Providing id and name of activeCardId.  
   * @Aim To call performance table API and for each card.
   */
  const handleSelectedScoreCard = (activeCardId: number, name:string, activeCardCount:number): void => {
    setActiveKPICardName(name)
    setactiveKPICardCount(activeCardCount)
    
    
    const payload: any = {
      ProgramYearId, 
      quarterMasterId, 
      programMasterId,  
    }
    
    if(name !== "all centres") {
      setSelectedKPICard(activeCardId); 
      payload.manageAwardId = activeCardId
    } 
    
    // Calling performance table to based on the selected card id
    fetchPerformanceTable({...payload})
  };

  const performanceWiseParametersDataSwitching = (activeBtnId: number, activeBtnName: string): void => {

    setActiveCentresPerformanceHeaderBtnId(activeBtnId);

    if(activeBtnName == "Performance parameter" ){
      // @ts-ignore
      setPerformIndicatorsGraphCategories(performanceIndicatorsGraphRes.performanceIndicatorsCategories);
      // @ts-ignore
      setPerformIndicatorsGraphSeriesData(performanceIndicatorsGraphRes.performanceIndicatorsSeriesData);
    }

    if(activeBtnName == "Quality parameter"){
      // @ts-ignore
      setPerformIndicatorsGraphCategories(performanceIndicatorsGraphRes.qualityAndEfficacyCategories);
      // @ts-ignore
      setPerformIndicatorsGraphSeriesData(performanceIndicatorsGraphRes.qualityAndEfficacySeriesData);
    }
  };

  const handleHitMapTablePagination = (event: React.ChangeEvent<unknown>, currPage: number): void => {
    setHitMapTablePagination((props:any) => ({
      ...props,
      pageNo: currPage.toString()
    }));
  }

  const toggleFilerFormDrawer = () => {
    setIsOpenFilterForm((preState) => !preState);
  };

  const handleApplyFilterBtn = (): void => {
    const { region, medals, state, city,  funder, centres, mcpCenter} = filter;

    const payload: Record<string, string | number | null> = {};

    if(region){
      payload.region = region;
    }

    
    if(medals && activeKPICardName !== activeCardStatusId.all_centres){
      payload.medals = medals?.id;
    }

    if(state){
      payload.state = state?.label;
    }
    if(city){
      payload.district = city?.id;
    }
    if(funder){
      payload.funder = funder?.id;
    }
    if(centres){
      payload.centerId = centres?.id;
    }
    if(mcpCenter){
      payload.mcpCenter = mcpCenter?.id;
    }
    
    setCommonApiPayload(payload);   // Applying filter to the performance table
    toggleFilerFormDrawer();        // Calling filter drawer function
  };

  const handleClearAllFilterBtn = (): void => {

    setFilter({
      center: "",
      medals: "",
      state: "",
      city: "",
      // regionalManager: "",
      // cityManager: "",
      // clusterManager: "",
      funder: "",
      region: "",
      mcpCenter: "",
    });

    setCommonApiPayload({
      region: null,
      medals:  null,
      state: null,
      city: null,
      reginalDataManager: null,
      cityManager: null,
      clusterManager: null,
      funder:  null,
      centerId: null,
      mcpCenter: null,
    });

    toggleFilerFormDrawer();
  };

  const handleAllPerformanceTableSearch = (value: string | number): void => {
    const searchedValue: string = value.toString();

    if (searchedValue.length >= 1 && searchedValue.length <= 3) {
      setIsInvalidCentresPerformanceTabSearch(true);
    }else {
      setIsInvalidCentresPerformanceTabSearch(false);
    }
    
    if(searchedValue.length > 3){
      setCentresPerformanceTableSearch(searchedValue);
    }

    if(searchedValue.length == 0){
      setCentresPerformanceTableSearch("");
    }

  };


  const handleAllPerformanceTablePagination = (event: any, pageNo: number): void => {
    setCentresPerformanceTablePagination((props) => ({
      ...props,
      pageNo: pageNo
    }));
  };

  // handle click of header btn "Performance parameter" in  centres performance table 
  const handlePerformancesParameterBtnClick = (): void => {

    // @ts-ignore
    setCentresPerformanceTableData(performanceTableRes?.performanceTableRes);
    setActiveCentresPerformanceTable(performanceGraphType.performanceParameter);
  };

  // handle click of header btn "Quality parameter" in  centres performance table 
  const handleQualityParameterTable = (): void => {
  
    // @ts-ignore
    setCentresPerformanceTableData(performanceTableRes?.performanceTableRes);
    setActiveCentresPerformanceTable(performanceGraphType?.qualityParameter);
  };

  const handleFilterFormChange = (field: string, value: any) => {
    if (field === "state") {
      const indianCities: IndianCity[] = value?.id
        ? City.getCitiesOfState("IN", value?.id).map((city:any) => ({
            label: city.name,
            id: city.name,
          }))
        : [];

      setCities(indianCities);
    }
   
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      [field]: value,
    }));
  };

  useEffect(() => {  
    // @ts-ignore
    setCentresPerformanceTableData(performanceTableRes?.performanceTableRes);
    
  }, [performanceTableRes]); 




  const performanceGraphSwitchBtn = [
    {
      text: performanceGraphType.performanceParameter,
      variant: "text",
      startIcon: "",
      endIcon: "",
      onClick: handlePerformancesParameterBtnClick,
      disabled: false,
      loading: false,
      sx: {
        backgroundColor:
          activeCentresPerformanceTable === performanceGraphType.performanceParameter
            ? "#FFF1B7"
            : "#FFF", // Active background color for line
        color:
          activeCentresPerformanceTable === performanceGraphType.performanceParameter
            ? "#333"
            : "#6D6D6D", // Text color when active
        fontWeight: "600",
        border:
          activeCentresPerformanceTable === performanceGraphType.performanceParameter
            ? "1px solid #FFF1B7"
            : "1px solid #FFF",
        borderBottom:
          activeCentresPerformanceTable === performanceGraphType.performanceParameter
            ? "2px solid #333"
            : "2px solid #D0D5DD", // Highlight the active button
        cursor: "pointer",
        transition: "all 0.2s ease",
      },
    },

    {
      text: performanceGraphType.qualityParameter,
      variant: "text",
      startIcon: "",
      endIcon: "",
      onClick: handleQualityParameterTable,
      disabled: false,
      loading: false,
      sx: {
        backgroundColor:
          activeCentresPerformanceTable === performanceGraphType.qualityParameter
            ? "#FFF1B7"
            : "#FFF",
        color:
          activeCentresPerformanceTable === performanceGraphType.qualityParameter
            ? "#333"
            : "#6D6D6D",
        fontWeight: "600",
        border:
          activeCentresPerformanceTable === performanceGraphType.qualityParameter
            ? "1px solid #FFF"
            : "1px solid #FFF1B7",
        borderBottom:
          activeCentresPerformanceTable === performanceGraphType.qualityParameter
            ? "2px solid #333"
            : "2px solid #D0D5DD",
        cursor: "pointer",
        transition: "all 0.2s ease", // Smooth transition
      },
    },
  ];



  return (
    <>
      <Stack sx={{ padding: "0px 20px 0px 20px" }}>
      {dashboardKPIAccess?.canRead &&
        <KPICards
          cardList={kpiCardData}
          isLoading={isKPICardLoading}
          selectedCardStatus={activeKPICardName}
          handleSelectedScoreCard={handleSelectedScoreCard}
          handleFilter={toggleFilerFormDrawer}
        />
        }

        <Box pb="40px">

          {/* All Graphs visible when all centres cards are selected: */}
          {/* All ghraphs are hidden for remaining cards is selected: */}
          {activeKPICardName === activeCardStatusId.all_centres && (
            <>
            {regionWiseGraphAccess?.canRead && 
              <RegionWiseMedalsGraph
                seriesData={regionWiseModalsGraphRes} 
                isLoading={regionWiseModalsLoading} 
              />}

           {QuarterWiseMedalsGraphAccess?.canRead &&
              <QuarterWiseMedalsGraph 
                seriesData={quarterGraphData} 
                isLoading={isQuarterGraphLoading}
              />}
          
          {IndividualIndicatorsGraphAccess?.canRead &&
              <IndividualIndicatorsGraph
                chartData={individualIndicatorsGraphRes}
                quarters={individualIndicatorsGraphQuarters}
                isLoading = {isIndividualIndicatorGraphLoading}
              />}

           {PerformanceIndicatorsGraphAccess?.canRead &&
              <PerformanceIndicatorsGraph
                chartData={performIndicatorsGraphSeriesData}
                isLoading={isPerformanceIndicatorsGraphLoading}
                performanceWiseStatusInfo={performanceWiseStatusInfo}
                performanceWiseParameterBtnInfo={performanceWiseParameterBtnInfo}
                performIndicatorsGraphCategories={performIndicatorsGraphCategories}
                activeCentresPerformanceHeaderBtnId={activeCentresPerformanceHeaderBtnId}
                performanceWiseParametersDataSwitching={performanceWiseParametersDataSwitching}
              />}

            {HeatMapTableAccess?.canRead &&
              <HeatMapTable
                data={hitMapData}
                isLoading={isHitMapTableLoading}
                heatMapTableStatusBtn = {heatMapTableStatusBtn}
                handleHitMapTablePagination = {handleHitMapTablePagination}
              />}
            </>
          )}
        </Box>

        {performanceOfAllCentresMedalsAccess?.canRead && 
        <Stack>
          <MagicPageHeader
            chipLabel={`${activeKPICardName === "all centres" ? totalCentersCount : activeKPICardCount}  centres`}
            title={`Performance of ${activeKPICardName} medals`}
            subTitle={`
              here you can view the performance of all
              ${activeKPICardName == "all centres" ? "centres" : activeKPICardName}
              medals
            `}
            sx={{marginTop: activeKPICardName == activeCardStatusId.all_centres ? "-23px" : "-37px"}}
            isLoading={isPerformanceTableLoading}
          />

          <MagicTableHeader
            sx={{marginBottom:"20px"}}
            buttons={performanceGraphSwitchBtn}
            isLoading={isPerformanceTableLoading}
            isInvalidSearch={isInvalidCentresPerformanceTabSearch}
            onSearchfieldChange={handleAllPerformanceTableSearch}
          />

          <CentresPerformanceTable
            rowData={centresPerformanceTableData}
            isLoading={isPerformanceTableLoading}
            handlePaginationChange={handleAllPerformanceTablePagination}
            activePaginationPageNo={centresPerformanceTablePagination.pageNo}
            activeCentresPerformanceTable={activeCentresPerformanceTable}
          />
        </Stack>}
        
      </Stack>
      

      
      {/* Add Filter SideDrawer */}
      <MagicDrawer
        title="Add Filters"
        subtitle="See the data in an organized manner by applying filters"
        drawerOpen={isOpenFilterForm}
        onDrawerToggle={toggleFilerFormDrawer}
        actionComponent={() => (
          <>
            <Button
              className="bun-outline"
              onClick={handleClearAllFilterBtn}
            >
              Clear All
            </Button>
            <Button
              className="bun-filled"
              onClick={handleApplyFilterBtn}
            >
              Done
            </Button>
          </>
        )}
      >
        <FilterForm
          data = {filterFormValueAndLabel}
          ratioBtn={ratioBtn}
          filter={filter}
          handleFilterFormChange={handleFilterFormChange}
          isDisableMedalsField = {activeKPICardName === activeCardStatusId.all_centres}
        />
      </MagicDrawer>
    </>
  );
};

export default DashboardContainer;
